import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type ImportOutcome, type ReportKind } from '../api/client.js';
import { parseFilenameDate, parseFilenamePeriod } from '../lib/filename-period.js';
import {
  addFiles,
  clearAll,
  duplicateDayIds,
  hasValidPeriod,
  removeItem,
  retryItem,
  runQueue,
  setOverrideKind,
  setPeriod,
  setReportDate,
  totals,
  type DatePolicy,
  type QueueItem,
  type UploadResult,
} from '../lib/upload-queue.js';

/**
 * The upload queue, lifted above the router so a running batch survives leaving
 * the Imports page and back. The nav shows a live indicator while it runs; the
 * Imports page renders the full queue table from the same state.
 */

/** Which kinds need a per-file date, and of what shape. */
export function datePolicyFor(kind: ReportKind): DatePolicy {
  if (kind === 'business_by_child_asin') return 'range';
  if (
    kind === 'fba_inventory' ||
    kind === 'inventory_health' ||
    kind === 'restock_recommendations' ||
    kind === 'all_listings'
  ) {
    return 'day';
  }
  return 'none';
}

/** A YYYY-MM-DD in the viewer's own zone. */
function localDay(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

interface QueueValue {
  kind: ReportKind;
  setKind: (kind: ReportKind) => void;
  datePolicy: DatePolicy;
  items: QueueItem<File>[];
  running: boolean;
  stopRequested: boolean;
  finished: boolean;
  duplicateDates: Set<string>;
  totals: ReturnType<typeof totals>;
  canStart: boolean;
  /** Progress for the nav indicator while a batch runs. */
  progress: { current: number; total: number } | null;
  lastFinishedKind: ReportKind | null;
  addPickedFiles: (files: FileList | File[]) => void;
  setRowPeriod: (id: string, period: { start?: string; end?: string }) => void;
  setRowDate: (id: string, day: string) => void;
  remove: (id: string) => void;
  retry: (id: string) => void;
  replaceRow: (id: string) => void;
  /** Re-queue a wrong-kind row under the kind the server suggested. */
  uploadAs: (id: string, kind: ReportKind) => void;
  clear: () => void;
  start: () => void;
  requestStop: () => void;
}

const QueueCtx = createContext<QueueValue | null>(null);

export function useUploadQueue(): QueueValue {
  const value = useContext(QueueCtx);
  if (!value) throw new Error('useUploadQueue must be used inside <UploadQueueProvider>.');
  return value;
}

export function UploadQueueProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const [kind, setKindState] = useState<ReportKind>('all_listings');
  const [items, setItems] = useState<QueueItem<File>[]>([]);
  const [running, setRunning] = useState(false);
  const [stopRequested, setStopRequested] = useState(false);
  const [finished, setFinished] = useState(false);
  const [lastFinishedKind, setLastFinishedKind] = useState<ReportKind | null>(null);

  const runningRef = useRef(false);
  const stopRef = useRef(false);
  const idRef = useRef(0);
  const replaceRef = useRef<Set<string>>(new Set());

  const datePolicy = datePolicyFor(kind);
  const makeId = () => `q${(idRef.current += 1)}`;

  const addPickedFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setFinished(false);
      setItems((prev) =>
        addFiles(prev, list, {
          datePolicy,
          makeId,
          parsePeriod: parseFilenamePeriod,
          parseDate: parseFilenameDate,
          zoneDay: localDay,
          today: localDay(Date.now()),
        }),
      );
    },
    [datePolicy],
  );

  const setKind = useCallback((next: ReportKind) => {
    setKindState(next);
  }, []);

  async function uploadRow(item: QueueItem<File>): Promise<UploadResult> {
    const form = new FormData();
    // A row re-queued by "Upload as …" carries its own kind; every other row
    // uploads under the batch's kind. Dates follow the kind actually used.
    const effectiveKind = (item.overrideKind as ReportKind | null) ?? kind;
    const policy = datePolicyFor(effectiveKind);
    form.set('kind', effectiveKind);
    if (policy !== 'none') {
      form.set('periodStart', item.periodStart);
      form.set('periodEnd', item.periodEnd);
    }
    if (replaceRef.current.has(item.id)) form.set('replace', 'true');
    form.set('file', item.file, item.name);
    try {
      const outcome = await api.upload<ImportOutcome>('/api/imports', form);
      return {
        kind: 'imported',
        counts: outcome.counts,
        rowCount: outcome.file.rowCount,
        warnings: outcome.warnings ?? [],
      };
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'DUPLICATE_FILE') {
        const day = caught.body?.existingUploadedDay ?? caught.body?.existingUploadedAt;
        return {
          kind: 'duplicate',
          duplicateDate: typeof day === 'string' ? day.slice(0, 10) : null,
        };
      }
      const suggested =
        caught instanceof ApiError && caught.code === 'WRONG_KIND'
          ? caught.body?.suggestedKind
          : null;
      return {
        kind: 'failed',
        error: caught instanceof ApiError ? caught.message : 'The upload failed.',
        code: caught instanceof ApiError ? (caught.code ?? null) : null,
        suggestedKind: typeof suggested === 'string' ? suggested : null,
      };
    }
  }

  const start = useCallback(() => {
    if (runningRef.current) return; // a second start while running is ignored
    runningRef.current = true;
    stopRef.current = false;
    setRunning(true);
    setStopRequested(false);
    setFinished(false);

    void (async () => {
      let latest = items;
      await runQueue(
        items,
        (item) => uploadRow(item),
        () => stopRef.current,
        (next) => {
          latest = next;
          setItems(next);
        },
      );
      replaceRef.current = new Set();
      runningRef.current = false;
      setRunning(false);
      setFinished(true);
      setLastFinishedKind(kind);
      // Show the tab of the kind just uploaded.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', kind);
          return next;
        },
        { replace: true },
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['imports'] }),
        queryClient.invalidateQueries({ queryKey: ['coverage'] }),
        queryClient.invalidateQueries({ queryKey: ['summary'] }),
        queryClient.invalidateQueries({ queryKey: ['skus'] }),
        queryClient.invalidateQueries({ queryKey: ['restock'] }),
      ]);
      void latest;
    })();
    // uploadRow closes over the current kind/datePolicy, which are stable while
    // a run is on (the controls are disabled).
  }, [items, kind, datePolicy, queryClient, setSearchParams]);

  const requestStop = useCallback(() => {
    stopRef.current = true;
    setStopRequested(true);
  }, []);

  // Warn before leaving with a run in progress, wherever in the app the user is.
  useEffect(() => {
    if (!running) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [running]);

  const replaceRow = useCallback((id: string) => {
    replaceRef.current.add(id);
    setItems((prev) => retryItem(prev, id));
  }, []);

  // Re-queue one wrong-kind row under the suggested kind, as a per-row override:
  // only this row changes, so the chooser and every other row (including leftover
  // queued rows after a Stop) keep the kind they were added under. Its date
  // inputs and the Upload gate follow the override kind's own policy.
  const uploadAs = useCallback((id: string, next: ReportKind) => {
    setItems((prev) => setOverrideKind(prev, id, next));
  }, []);

  const value: QueueValue = useMemo(() => {
    const duplicateDates = duplicateDayIds(items);
    const t = totals(items);
    const batchTotal = t.total - t.tooLarge;
    const progress = running
      ? {
          current: Math.min(t.imported + t.duplicate + t.failed + 1, batchTotal),
          total: batchTotal,
        }
      : null;
    // Every queued row must satisfy its OWN kind's date policy — the batch kind,
    // or its per-row override — and no two day-kind rows may share a date.
    const queued = items.filter((item) => item.status === 'queued');
    const datesReady = queued.every((item) => {
      const policy = datePolicyFor((item.overrideKind as ReportKind | null) ?? kind);
      if (policy === 'range') return hasValidPeriod(item);
      if (policy === 'day') return item.periodStart !== '';
      return true;
    });
    const canStart = !running && queued.length > 0 && datesReady && duplicateDates.size === 0;
    return {
      kind,
      setKind,
      datePolicy,
      items,
      running,
      stopRequested,
      finished,
      duplicateDates,
      totals: t,
      canStart,
      progress,
      lastFinishedKind,
      addPickedFiles,
      setRowPeriod: (id, period) => setItems((prev) => setPeriod(prev, id, period)),
      setRowDate: (id, day) => setItems((prev) => setReportDate(prev, id, day)),
      remove: (id) => setItems((prev) => removeItem(prev, id)),
      retry: (id) => setItems((prev) => retryItem(prev, id)),
      replaceRow,
      uploadAs,
      clear: () => setItems((prev) => clearAll(prev)),
      start,
      requestStop,
    };
  }, [
    kind,
    setKind,
    datePolicy,
    items,
    running,
    stopRequested,
    finished,
    lastFinishedKind,
    addPickedFiles,
    replaceRow,
    uploadAs,
    start,
    requestStop,
  ]);

  return <QueueCtx.Provider value={value}>{children}</QueueCtx.Provider>;
}
